import React, {useEffect, useRef, useState} from "react";
import DataTable from "react-data-table-component";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronLeft, faChevronRight} from "@fortawesome/free-solid-svg-icons";
import useIsMobile from "../../hooks/useIsMobile";

/**
 * Responsive results table for the Reports page.
 *
 * Desktop (> 768px): renders react-data-table-component untouched — every
 * prop is forwarded, so the dense grid, server pagination, sorting and
 * expandable rows behave exactly as before.
 *
 * Mobile (<= 768px): the fixed-header table model breaks down (a scroll
 * container with no resolvable height hides most rows). Instead of fighting
 * that with CSS, we render a purpose-built vertical CARD list straight from
 * the SAME `columns` + `data` the table already gets — so every field is
 * labelled, every row is a self-contained card, and the whole list flows in
 * normal document order. Scrolling is the page's own scroll: nothing is
 * clipped, every row is reachable. Server pagination gets a compact,
 * touch-friendly pager.
 */
export default function ReportTable(props) {
  const isMobile = useIsMobile(768);
  if (!isMobile) return <DataTable {...props} />;
  return <MobileReportCards {...props} />;
}

function cellValue(col, row, index) {
  if (typeof col.cell === "function") return col.cell(row, index, col, index);
  if (typeof col.selector === "function") return col.selector(row);
  return null;
}

function MobileReportCards({
  columns = [],
  data = [],
  keyField = "id",
  progressPending = false,
  progressComponent = null,
  noDataComponent = null,
  expandableRows = false,
  expandableRowsComponent: Expanded = null,
  pagination = false,
  paginationTotalRows = 0,
  paginationPerPage = 25,
  paginationDefaultPage = 1,
  paginationRowsPerPageOptions = [25, 50, 100],
  onChangePage,
  onChangeRowsPerPage,
}) {
  const visibleColumns = columns.filter((c) => !c.omit && (c.name || c.cell || c.selector));

  if (progressPending) {
    return <div className="report-cards-state">{progressComponent}</div>;
  }
  if (!data || data.length === 0) {
    return <div className="report-cards-state">{noDataComponent}</div>;
  }

  return (
    <div className="report-cards">
      {data.map((row, index) => {
        const rowKey = row?.[keyField] ?? index;
        return (
          <RecordCard
            key={rowKey}
            row={row}
            index={index}
            columns={visibleColumns}
            Expanded={Expanded}
            hasExpandable={expandableRows && !!Expanded}
          />
        );
      })}

      {pagination && typeof onChangePage === "function" && (
        <MobilePager
          totalRows={paginationTotalRows}
          perPage={paginationPerPage}
          defaultPage={paginationDefaultPage}
          rowsPerPageOptions={paginationRowsPerPageOptions}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
        />
      )}
    </div>
  );
}

function RecordCard({row, index, columns, Expanded, hasExpandable}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="report-card">
      <dl className="report-card-fields">
        {columns.map((col) => {
          const value = cellValue(col, row, index);
          if (value === null || value === undefined || value === "") return null;
          return (
            <div className="report-card-field" data-col={col.id || col.name} key={col.id || col.name}>
              <dt className="report-card-label">{col.name}</dt>
              <dd className="report-card-value">{value}</dd>
            </div>
          );
        })}
      </dl>

      {hasExpandable && (
        <button
          type="button"
          className={`report-card-expand ${open ? "is-open" : ""}`}
          onClick={() => setOpen((v) => !v)}
        >
          <FontAwesomeIcon icon={faChevronDown} />
          {open ? "Hide details" : "View details"}
        </button>
      )}

      {hasExpandable && open && (
        <div className="report-card-details">
          <Expanded data={row} />
        </div>
      )}
    </div>
  );
}

function MobilePager({totalRows, perPage, defaultPage, rowsPerPageOptions, onChangePage, onChangeRowsPerPage}) {
  const [page, setPage] = useState(defaultPage || 1);
  const [rows, setRows] = useState(perPage || 25);
  const pagerRef = useRef(null);

  // Keep in sync if the parent drives the page/size from its own state.
  useEffect(() => {
    if (defaultPage) setPage(defaultPage);
  }, [defaultPage]);
  useEffect(() => {
    if (perPage) setRows(perPage);
  }, [perPage]);

  const totalPages = Math.max(1, Math.ceil((totalRows || 0) / (rows || 1)));

  // The card list is one continuous page-level scroll on mobile, and the
  // pager sits at its bottom — after switching pages the new rows render
  // above the fold, off-screen, while the scroll position stays put. It
  // looks like the tap did nothing. Bring the top of the card list back
  // into view so the freshly-fetched page is actually visible.
  const scrollResultsIntoView = () => {
    const list = pagerRef.current?.closest(".report-cards");
    list?.scrollIntoView({behavior: "smooth", block: "start"});
  };

  const goTo = (next) => {
    const clamped = Math.min(Math.max(1, next), totalPages);
    if (clamped === page) return;
    setPage(clamped);
    onChangePage?.(clamped, totalRows);
    scrollResultsIntoView();
  };

  const changeSize = (value) => {
    const size = Number(value);
    setRows(size);
    setPage(1);
    onChangeRowsPerPage?.(size, 1);
    scrollResultsIntoView();
  };

  return (
    <div className="report-pager" ref={pagerRef}>
      <div className="report-pager-rows">
        <span>Rows</span>
        <select value={rows} onChange={(e) => changeSize(e.target.value)}>
          {rowsPerPageOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      <div className="report-pager-nav">
        <button type="button" onClick={() => goTo(page - 1)} disabled={page <= 1} aria-label="Previous page">
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <span className="report-pager-status">
          Page {page} / {totalPages}
        </span>
        <button type="button" onClick={() => goTo(page + 1)} disabled={page >= totalPages} aria-label="Next page">
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    </div>
  );
}
